/**
 * UBO Ownership Visualization Component
 *
 * Renders a visual ownership structure graph showing the company
 * at the top with beneficial owners connected below by ownership lines.
 * Uses pure CSS/SVG - no external graph libraries.
 */

import React, { useRef, useEffect, useState } from 'react';
import {
  Users,
  Shield,
} from 'lucide-react';

const OWNERSHIP_LINE_STYLES = {
  direct: { dashArray: 'none', label: 'Direct' },
  indirect: { dashArray: '8,4', label: 'Indirect' },
  control: { dashArray: '3,3', label: 'Control' },
};

function getNodeBorderColor(ubo) {
  if (ubo.screening_status === 'hits' || (ubo.flags && ubo.flags.length > 0)) {
    return '#ef4444';
  }
  if (ubo.verification_status === 'verified' || ubo.verification_status === 'linked') {
    return '#22c55e';
  }
  return '#f59e0b';
}

export default function UBOVisualization({ ubos = [], companyName = 'Company' }) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function updateDimensions() {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setDimensions({ width, height: 0 });
        setIsMobile(width < 600);
      }
    }
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  if (!ubos || ubos.length === 0) {
    return (
      <div className="ubo-viz">
        <style>{vizStyles}</style>
        <div className="ubo-viz__empty">
          <Users size={40} />
          <p>No beneficial owners recorded</p>
        </div>
      </div>
    );
  }

  // Layout calculations
  const companyNodeWidth = 200;
  const companyNodeHeight = 64;
  const uboNodeWidth = 220;
  const uboNodeHeight = 120;
  const verticalGap = 80;
  const horizontalGap = 24;

  if (isMobile) {
    // Vertical stacked layout for mobile
    const svgHeight = companyNodeHeight + verticalGap + (ubos.length * (uboNodeHeight + 16)) + 20;
    const svgWidth = Math.max(dimensions.width - 32, uboNodeWidth + 40);
    const centerX = svgWidth / 2;
    const companyX = centerX - companyNodeWidth / 2;
    const companyY = 16;
    const companyCenterX = companyX + companyNodeWidth / 2;
    const companyCenterY = companyY + companyNodeHeight;

    return (
      <div className="ubo-viz" ref={containerRef}>
        <style>{vizStyles}</style>
        <div className="ubo-viz__header">
          <Shield size={16} />
          <span>Ownership Structure</span>
        </div>
        <div className="ubo-viz__canvas">
          <svg
            width="100%"
            height={svgHeight}
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            preserveAspectRatio="xMidYMin meet"
          >
            {/* Company node */}
            <CompanyNode
              x={companyX}
              y={companyY}
              width={companyNodeWidth}
              height={companyNodeHeight}
              name={companyName}
            />

            {/* UBO nodes - stacked vertically */}
            {ubos.map((ubo, index) => {
              const uboX = centerX - uboNodeWidth / 2;
              const uboY = companyY + companyNodeHeight + verticalGap + index * (uboNodeHeight + 16);
              const lineStyle = OWNERSHIP_LINE_STYLES[ubo.ownership_type] || OWNERSHIP_LINE_STYLES.direct;
              const borderColor = getNodeBorderColor(ubo);
              const uboCenterX = uboX + uboNodeWidth / 2;
              const uboCenterY = uboY;

              return (
                <g key={ubo.id}>
                  {/* Connecting line */}
                  <line
                    x1={companyCenterX}
                    y1={companyCenterY}
                    x2={uboCenterX}
                    y2={uboCenterY}
                    stroke={borderColor}
                    strokeWidth="2"
                    strokeDasharray={lineStyle.dashArray}
                    opacity="0.6"
                  />
                  {/* Ownership % label on line */}
                  {ubo.ownership_percentage != null && (
                    <g>
                      <rect
                        x={Math.min(companyCenterX, uboCenterX) + Math.abs(companyCenterX - uboCenterX) / 2 - 28}
                        y={(companyCenterY + uboCenterY) / 2 - 10}
                        width="56"
                        height="20"
                        rx="10"
                        fill="var(--bg-primary, #0a0b0e)"
                        stroke={borderColor}
                        strokeWidth="1"
                        opacity="0.9"
                      />
                      <text
                        x={Math.min(companyCenterX, uboCenterX) + Math.abs(companyCenterX - uboCenterX) / 2}
                        y={(companyCenterY + uboCenterY) / 2 + 4}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="600"
                        fill="var(--text-primary, #f0f2f5)"
                      >
                        {ubo.ownership_percentage}%
                      </text>
                    </g>
                  )}
                  {/* UBO node */}
                  <UBONode
                    x={uboX}
                    y={uboY}
                    width={uboNodeWidth}
                    height={uboNodeHeight}
                    ubo={ubo}
                    borderColor={borderColor}
                  />
                </g>
              );
            })}
          </svg>
        </div>
        <div className="ubo-viz__legend">
          <LegendItem dashArray="none" label="Direct" />
          <LegendItem dashArray="8,4" label="Indirect" />
          <LegendItem dashArray="3,3" label="Control" />
        </div>
      </div>
    );
  }

  // Desktop horizontal layout
  const totalUBOWidth = ubos.length * uboNodeWidth + (ubos.length - 1) * horizontalGap;
  const svgWidth = Math.max(totalUBOWidth + 40, companyNodeWidth + 40, dimensions.width - 32);
  const svgHeight = companyNodeHeight + verticalGap + uboNodeHeight + 40;
  const centerX = svgWidth / 2;
  const companyX = centerX - companyNodeWidth / 2;
  const companyY = 16;
  const companyCenterX = companyX + companyNodeWidth / 2;
  const companyCenterY = companyY + companyNodeHeight;
  const uboStartX = centerX - totalUBOWidth / 2;

  return (
    <div className="ubo-viz" ref={containerRef}>
      <style>{vizStyles}</style>
      <div className="ubo-viz__header">
        <Shield size={16} />
        <span>Ownership Structure</span>
      </div>
      <div className="ubo-viz__canvas">
        <svg
          width="100%"
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          preserveAspectRatio="xMidYMin meet"
        >
          {/* Company node */}
          <CompanyNode
            x={companyX}
            y={companyY}
            width={companyNodeWidth}
            height={companyNodeHeight}
            name={companyName}
          />

          {/* UBO nodes */}
          {ubos.map((ubo, index) => {
            const uboX = uboStartX + index * (uboNodeWidth + horizontalGap);
            const uboY = companyY + companyNodeHeight + verticalGap;
            const lineStyle = OWNERSHIP_LINE_STYLES[ubo.ownership_type] || OWNERSHIP_LINE_STYLES.direct;
            const borderColor = getNodeBorderColor(ubo);
            const uboCenterX = uboX + uboNodeWidth / 2;
            const uboCenterY = uboY;

            // Mid point for label
            const midX = (companyCenterX + uboCenterX) / 2;
            const midY = (companyCenterY + uboCenterY) / 2;

            return (
              <g key={ubo.id}>
                {/* Connecting line */}
                <line
                  x1={companyCenterX}
                  y1={companyCenterY}
                  x2={uboCenterX}
                  y2={uboCenterY}
                  stroke={borderColor}
                  strokeWidth="2"
                  strokeDasharray={lineStyle.dashArray}
                  opacity="0.6"
                />
                {/* Ownership % label on line */}
                {ubo.ownership_percentage != null && (
                  <g>
                    <rect
                      x={midX - 28}
                      y={midY - 10}
                      width="56"
                      height="20"
                      rx="10"
                      fill="var(--bg-primary, #0a0b0e)"
                      stroke={borderColor}
                      strokeWidth="1"
                      opacity="0.9"
                    />
                    <text
                      x={midX}
                      y={midY + 4}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="600"
                      fill="var(--text-primary, #f0f2f5)"
                    >
                      {ubo.ownership_percentage}%
                    </text>
                  </g>
                )}
                {/* UBO node */}
                <UBONode
                  x={uboX}
                  y={uboY}
                  width={uboNodeWidth}
                  height={uboNodeHeight}
                  ubo={ubo}
                  borderColor={borderColor}
                />
              </g>
            );
          })}
        </svg>
      </div>
      <div className="ubo-viz__legend">
        <LegendItem dashArray="none" label="Direct" />
        <LegendItem dashArray="8,4" label="Indirect" />
        <LegendItem dashArray="3,3" label="Control" />
      </div>
    </div>
  );
}

function CompanyNode({ x, y, width, height, name }) {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx="12"
        fill="var(--bg-secondary, #111318)"
        stroke="var(--accent-primary, #6366f1)"
        strokeWidth="2.5"
      />
      {/* Building icon area */}
      <rect
        x={x + 12}
        y={y + (height / 2) - 12}
        width="24"
        height="24"
        rx="4"
        fill="var(--accent-primary, #6366f1)"
        opacity="0.15"
      />
      {/* Simple building icon shape */}
      <rect
        x={x + 19}
        y={y + (height / 2) - 6}
        width="10"
        height="12"
        rx="1"
        fill="var(--accent-primary, #6366f1)"
        opacity="0.8"
      />
      <rect x={x + 21} y={y + (height / 2) - 3} width="2" height="2" rx="0.5" fill="var(--bg-secondary, #111318)" />
      <rect x={x + 25} y={y + (height / 2) - 3} width="2" height="2" rx="0.5" fill="var(--bg-secondary, #111318)" />
      <rect x={x + 21} y={y + (height / 2) + 1} width="2" height="2" rx="0.5" fill="var(--bg-secondary, #111318)" />
      <rect x={x + 25} y={y + (height / 2) + 1} width="2" height="2" rx="0.5" fill="var(--bg-secondary, #111318)" />
      {/* Company name */}
      <text
        x={x + 44}
        y={y + height / 2 + 5}
        fontSize="13"
        fontWeight="600"
        fill="var(--text-primary, #f0f2f5)"
      >
        {name.length > 18 ? name.slice(0, 17) + '...' : name}
      </text>
    </g>
  );
}

function UBONode({ x, y, width, height, ubo, borderColor }) {
  const roles = [];
  if (ubo.is_director) roles.push('Director');
  if (ubo.is_shareholder) roles.push('Shareholder');
  if (ubo.is_signatory) roles.push('Signatory');

  const flags = ubo.flags || [];
  const hasSanctionsFlag = flags.some((f) => f.toLowerCase().includes('sanction'));
  const hasPEPFlag = flags.some((f) => f.toLowerCase().includes('pep'));

  return (
    <g>
      {/* Node background */}
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx="10"
        fill="var(--bg-secondary, #111318)"
        stroke={borderColor}
        strokeWidth="2"
      />

      {/* Name */}
      <text
        x={x + 12}
        y={y + 22}
        fontSize="13"
        fontWeight="600"
        fill="var(--text-primary, #f0f2f5)"
      >
        {ubo.full_name && ubo.full_name.length > 22
          ? ubo.full_name.slice(0, 21) + '...'
          : ubo.full_name || 'Unknown'}
      </text>

      {/* Role title / nationality */}
      <text
        x={x + 12}
        y={y + 38}
        fontSize="11"
        fill="var(--text-secondary, #8b919e)"
      >
        {[ubo.role_title, ubo.nationality].filter(Boolean).join(' | ') || '\u00A0'}
      </text>

      {/* Voting rights if present */}
      {ubo.voting_rights_percentage != null && (
        <text
          x={x + 12}
          y={y + 52}
          fontSize="10"
          fill="var(--text-secondary, #8b919e)"
        >
          Voting: {ubo.voting_rights_percentage}%
        </text>
      )}

      {/* Role badges */}
      {roles.length > 0 && (
        <g>
          {roles.map((role, i) => {
            const badgeX = x + 12 + i * 72;
            const badgeY = y + height - 38;
            return (
              <g key={role}>
                <rect
                  x={badgeX}
                  y={badgeY}
                  width="66"
                  height="18"
                  rx="9"
                  fill="var(--accent-primary, #6366f1)"
                  opacity="0.15"
                />
                <text
                  x={badgeX + 33}
                  y={badgeY + 12}
                  textAnchor="middle"
                  fontSize="9"
                  fontWeight="600"
                  fill="var(--accent-primary, #6366f1)"
                >
                  {role}
                </text>
              </g>
            );
          })}
        </g>
      )}

      {/* Flag badges (sanctions, PEP) */}
      {(hasSanctionsFlag || hasPEPFlag) && (
        <g>
          {hasSanctionsFlag && (
            <g>
              <rect
                x={x + width - 80}
                y={y + 6}
                width="72"
                height="18"
                rx="9"
                fill="rgba(239, 68, 68, 0.15)"
              />
              <text
                x={x + width - 44}
                y={y + 18}
                textAnchor="middle"
                fontSize="9"
                fontWeight="700"
                fill="#ef4444"
              >
                SANCTIONS
              </text>
            </g>
          )}
          {hasPEPFlag && (
            <g>
              <rect
                x={x + width - (hasSanctionsFlag ? 142 : 80)}
                y={y + 6}
                width="56"
                height="18"
                rx="9"
                fill="rgba(239, 68, 68, 0.15)"
              />
              <text
                x={x + width - (hasSanctionsFlag ? 114 : 52)}
                y={y + 18}
                textAnchor="middle"
                fontSize="9"
                fontWeight="700"
                fill="#ef4444"
              >
                PEP
              </text>
            </g>
          )}
        </g>
      )}

      {/* Verification status indicator dot */}
      <circle
        cx={x + width - 14}
        cy={y + height - 14}
        r="5"
        fill={borderColor}
        opacity="0.8"
      />
    </g>
  );
}

function LegendItem({ dashArray, label }) {
  return (
    <div className="ubo-viz__legend-item">
      <svg width="32" height="12">
        <line
          x1="0"
          y1="6"
          x2="32"
          y2="6"
          stroke="var(--text-secondary, #8b919e)"
          strokeWidth="2"
          strokeDasharray={dashArray}
        />
      </svg>
      <span>{label}</span>
    </div>
  );
}

const vizStyles = `
  .ubo-viz {
    margin-bottom: 20px;
  }

  .ubo-viz__header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-secondary, #8b919e);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 12px;
  }

  .ubo-viz__header svg {
    width: 16px;
    height: 16px;
    color: var(--accent-primary, #6366f1);
  }

  .ubo-viz__canvas {
    background: var(--bg-primary, #0a0b0e);
    border: 1px solid var(--border-primary, #23262f);
    border-radius: 10px;
    padding: 4px;
    overflow-x: auto;
  }

  .ubo-viz__canvas svg {
    display: block;
    min-width: 100%;
  }

  .ubo-viz__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    color: var(--text-secondary, #8b919e);
    background: var(--bg-primary, #0a0b0e);
    border: 1px solid var(--border-primary, #23262f);
    border-radius: 10px;
    gap: 12px;
  }

  .ubo-viz__empty svg {
    opacity: 0.5;
  }

  .ubo-viz__empty p {
    margin: 0;
    font-size: 14px;
  }

  .ubo-viz__legend {
    display: flex;
    gap: 20px;
    justify-content: center;
    margin-top: 10px;
    padding: 8px 0;
  }

  .ubo-viz__legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--text-secondary, #8b919e);
  }

  @media (max-width: 600px) {
    .ubo-viz__legend {
      flex-wrap: wrap;
      gap: 12px;
    }
  }
`;
